import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';

import { AgGridReact } from 'ag-grid-react';
import { notification } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles/dist/feature-toggles';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import useDeepCompareEffect from 'use-deep-compare-effect';
import {
  resetDeleteSuccess,
} from '../../common/toolbar/containers/toolbar-dynamic-actions-actions';
import { viewTypes } from '../../utils/app-configs';
import {
  getWorklistData,
  getWorklistDataNext,
  refreshWorkgrid,
  setChildDetailParams,
  setChildSelection,
  setMappedColumnDefs,
  triggerRowHighlight,
  updateDefaultExpand,
} from '../../worklist/containers/worklist-actions';
import {
  addCheckbox,
  addIcons,
  addMasterDetail,
  createServerSideDatasource,
  detailCellRendererParams,
  getAllExpandedRows,
  getRowHeight,
  handleCellClicked,
  handleColumnVisibilityEvents,
  handleFirstRender,
  handleSelectionChange,
  isRowSelectable,
  makeDraggable,
  mapGridColumnDef,
  setOrderColumns,
  smartActionsCellRendererParams,
} from '../helpers/grid-helpers';
import GridWrapper from './gridwrapper';
import PlanningCellRenderer from './planning-cellrenderer';
import SmartActionCellRenderer from './smartaction-cellrenderer';

const WorkGrid = ({
  columnActions = {},
  selectable = false,
  hasIconsColumn = false,
  rowSelection = 'multiple',
  rowHeight = 30,
  rowDragMultiRow = false,
  draggable = false,
  masterDetail = false,
  handleRowSelect,
  columnDef = [],
  isPlanningBoard = false,
  worklistId,
  isTickets = false,
  isMainTicketPage = false,
  fixedFilterValues = {},
  selectedIds,
}) => {
  const gridRef = useRef();
  const dispatch = useDispatch();
  const intl = useIntl();
  const [suppressRowDrag, setSuppressRowDrag] = useState(false);
  const [suppressDetailsRowDrag, setSuppressDetailsRowDrag] = useState(false);
  const [disableDetailSelection, setDisableDetailSelection] = useState(false);
  const [disableMasterSelection, setDisableMasterSelection] = useState(false);
  const [toggleViewChange, setToggleViewChange] = useState(false);
  const [columnParams, setColumnParams] = useState([]);
  const [dataSource, setDataSource] = useState();
  const [childColumnParams, setChildColumnParams] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [resetTable, setResetTable] = useState(false);
  const [resetChildToggle, setResetChildToggle] = useState(false);
  const [tableRowSelection, setTableRowSelection] = useState([]);
  const [prevView, setPrevView] = useState(null);
  const [prevUpdatedRowsData, setPrevUpdatedRowsData] = useState([]);

  const handleTableRowSelection = (selection) => {
    // save selection to hide quick actions in row
    setTableRowSelection(selection);
    handleRowSelect(selection);
  };

  const {
    activeFilters,
    activeSort,
    activeView,
    shouldRefetch,
    filterDefinitions = [],
    currentWorklistId,
    childColumnDetails,
    mappedColumns = [],
    refetchDataAfterVisualChange,
    childSelection,
    updatedRowsData,
    rowHighlightTrigger,
  } = useSelector(state => state.worklist);

  const defaultExpanded = useSelector(state =>
    state.worklist.defaultExpanded || false,
  );

  const { deleteSuccess } = useSelector(state => state.dynamicToolbar);

  const { childTableIds = [], childColumnDefinitions = [] } = useSelector(state =>
    state.worklist.metaData,
  );
  const page = useSelector(state =>
    state.worklist?.next?.query?.page || 0,
  );

  const isServerSideGroupOpenByDefault = ({ rowNode }) => expandedRows.includes(rowNode?.id);

  useDeepCompareEffect(() => {
    const selectedNodes = gridRef?.current?.api?.getSelectedNodes();
    if (selectedNodes) {
      selectedNodes.forEach((n) => {
        if (!selectedIds?.find(id => id === n.id) && n.selected) {
          n.setSelected(false);
        } else if (!n.selected) {
          n.setSelected(true);
        }
      });
    }
  }, [selectedIds]);

  useEffect(() => {
    if (deleteSuccess) {
      notification.success({
        key: 'delete-success',
        message: intl.formatMessage({ id: 'type.order.DRAFT_DELETE_SUCCESS' }),
        placement: 'top',
      });
      gridRef.current?.api?.refreshServerSideStore({ purge: true });
      dispatch(resetDeleteSuccess());
    }
  }, [deleteSuccess]);


  useDeepCompareEffect(() => {
    if (updatedRowsData.length || rowHighlightTrigger) {
      if (rowHighlightTrigger) {
        dispatch(triggerRowHighlight(false));
      }
      updatedRowsData.forEach((d) => {
        const rowNode = gridRef.current?.api?.getRowNode(d.id);
        if (rowNode) {
          if (d.isDeleted) {
            gridRef.current.api.applyServerSideTransaction({ remove: [rowNode.data] });
          } else {
            rowNode.highlighted = d.highlighted && !rowNode.expanded;
            rowNode.master = !!d.children?.length;
            if (!d.children?.length && rowNode.expanded) {
              rowNode.setExpanded(false);
              rowNode.highlighted = d.highlighted;
            } else if (d.children?.length && rowNode.detailNode) {
              rowNode.detailNode.setRowHeight((rowHeight + 2) * (d.children.length + 1));
              gridRef.current.api.onRowHeightChanged();
            }
            rowNode.setData(d);
          }
        }
      });
    } else {
      prevUpdatedRowsData.forEach((d) => {
        const rowNode = gridRef.current?.api?.getRowNode(d.id);
        if (rowNode && !d.isDeleted) {
          rowNode.highlighted = null;
          gridRef?.current?.api?.redrawRows({ rowNodes: [rowNode] });
        }
      });
    }
    setPrevUpdatedRowsData(updatedRowsData);
  }, [updatedRowsData, rowHighlightTrigger]);

  const createDataSource = () => {
    if (worklistId === currentWorklistId && activeView?.id) {
      if (gridRef) {
        setExpandedRows(getAllExpandedRows(gridRef?.current));
        gridRef.current?.api?.deselectAll();
        gridRef.current?.api?.forEachDetailGridInfo((detailGridInfo) => {
          detailGridInfo.api.deselectAll();
        });
        setDisableDetailSelection(false);
        setDisableMasterSelection(false);
      }
      const source = createServerSideDatasource(
        worklistId,
        p => dispatch(getWorklistData(p)),
        p => dispatch(getWorklistDataNext(p)),
        activeFilters,
        activeSort,
        mappedColumns?.length <= masterDetail + selectable + hasIconsColumn + draggable,
      );
      setDataSource(source);
    }
  };

  // Step 1: We get the columnDefinition and the activeView
  useDeepCompareEffect(() => {
    if (columnDef && activeView?.id) {
      if (!activeView?.isNone &&
        activeView?.templateType !== viewTypes.system &&
        activeView?.columnInfo?.length) {
        setColumnParams(activeView?.columnInfo?.map(c => (
          { ...c, tableId: c.tableId || worklistId }
        )));
      } else {
        const extraColumns = draggable + masterDetail + selectable;
        const filteredColumnDef = columnDef?.filter(c =>
          !c?.requiredFeatureFlag || isToggleEnabled(c?.requiredFeatureFlag));
        setColumnParams(filteredColumnDef?.map(c => ({
          id: c.id,
          index: c.sort + extraColumns,
          tableId: worklistId,
          width: null,
        })));
      }
      setToggleViewChange(true);
    }
  }, [activeView?.id, columnDef]);

  // Step 2: columnParams change, so we update the localstorage
  useDeepCompareEffect(() => {
    if (columnParams) {
      localStorage.setItem(
        'columnParams',
        JSON.stringify(columnParams
          ?.map(p => ({
            ...p,
            tableId: p.tableId || worklistId,
          }))
          ?.sort((a, b) => {
            if (a.index == null) {
              return 1;
            }
            return a.index - b.index;
          })),
      );
    }
  }, [columnParams, tableRowSelection]);

  // Step 3: When activeView changed, save/reset the specific view information + reset table
  useEffect(() => {
    if (toggleViewChange) {
      setResetTable(true);
      setPrevView(activeView);
      setToggleViewChange(false);
      dispatch(updateDefaultExpand(activeView?.isExpanded));
    }
  }, [toggleViewChange]);

  // Step 4: When toggle is activated, map the columns
  useEffect(() => {
    if (resetTable) {
      let columns = mapGridColumnDef ?
        mapGridColumnDef(
          columnDef,
          columnActions,
          false,
          'columnParams',
          activeSort,
          filterDefinitions,
          worklistId,
          draggable,
          fixedFilterValues,
          tableRowSelection,
          columnParams,
        ) :
        columnDef;
      if (masterDetail) {
        columns = addMasterDetail(columns);
      }
      if (selectable) {
        columns = addCheckbox(columns);
      }
      if (hasIconsColumn) {
        columns = addIcons(columns);
      }
      if (draggable) {
        columns = makeDraggable(columns, false);
      }

      columns = setOrderColumns(columns, 'columnParams', columnParams, false);
      dispatch(setMappedColumnDefs({ data: columns, refetch: true }));
      setResetTable(false);
    }
  }, [resetTable]);

  // Step 5: create data source with the mapped columns
  useDeepCompareEffect(() => {
    if (mappedColumns?.length) {
      if (refetchDataAfterVisualChange) {
        createDataSource();
      }

      setColumnParams(state =>
        [
          ...state,
          ...mappedColumns?.filter(mc =>
            !mc.isHidden &&
            !columnParams?.find(cp => cp.id === mc.colId) &&
            !mc.suppressMovable,
          )?.map(m => ({
            id: m.colId,
            tableId: m.tableId,
            width: m.width,
            index: m.index,
          })),
        ]
          ?.filter(cp => !mappedColumns?.find(mc => mc.colId === cp.id)?.isHidden)
          ?.map(p => ({
            ...p,
            index: mappedColumns?.find(mc => mc.colId === p.id)?.index,
            tableId: p.tableId || worklistId,
          }))
          ?.sort((a, b) => {
            if (a.index == null) {
              return 1;
            }
            return a.index - b.index;
          }),
      );
    }
  }, [mappedColumns]);

  useEffect(() => {
    // page will be at first load 2
    if (page > 2) {
      setResetTable(true);
    }
  }, [defaultExpanded]);

  // Reset when activeFilters changes but not when it's because of the change of view
  useDeepCompareEffect(() => {
    if (activeView?.id && activeView?.id === prevView?.id) {
      setResetTable(true);
    }
  }, [activeFilters]);

  // Reset when activeSort changes but not when it's because of the change of view
  useEffect(() => {
    if (activeSort && activeView?.id && activeView?.id === prevView?.id) {
      setResetTable(true);
    }
  }, [activeSort]);

  // Reload when PB actions are triggered
  useEffect(() => {
    if (shouldRefetch) {
      createDataSource();
      dispatch(refreshWorkgrid(false));
    }
  }, [shouldRefetch]);

  // Child Column Logic
  useDeepCompareEffect(() => {
    if (childColumnDefinitions?.length) {
      let childColumns = mapGridColumnDef ?
        mapGridColumnDef(
          childColumnDefinitions,
          columnActions,
          true,
          'childColumnParams',
          activeSort,
          filterDefinitions,
          worklistId,
          draggable,
          fixedFilterValues,
          childSelection,
        ) :
        childColumnDefinitions;

      if (selectable) {
        childColumns = addCheckbox(childColumns, true);
      }
      if (draggable) {
        childColumns = makeDraggable(childColumns, true);
      }

      childColumns = setOrderColumns(childColumns, 'childColumnParams', null, true);
      dispatch(setChildDetailParams(childColumns));
    }
  }, [childColumnDefinitions, resetChildToggle, childSelection, filterDefinitions]);

  useDeepCompareEffect(() => {
    if (childColumnDefinitions?.length && activeView?.id) {
      if (
        !activeView?.isNone &&
        activeView?.childColumnInfo
      ) {
        setChildColumnParams(activeView?.childColumnInfo);
      } else {
        const extraColumns = draggable + selectable;
        setChildColumnParams(childColumnDefinitions?.map((c, i) => ({
          id: c.id,
          tableId: c.childTableId,
          width: c.width || 175,
          index: i + extraColumns,
        })));
      }
    }
  }, [activeView?.id, childColumnDefinitions, childSelection]);

  useDeepCompareEffect(() => {
    if (childColumnParams?.length) {
      localStorage.setItem(
        'childColumnParams',
        JSON.stringify(childColumnParams
          ?.map(p => ({
            ...p,
            width: p.width || 175,
            tableId: p.tableId || childColumnDefinitions?.find(c => c.id === p.id)?.childTableId,
          }))
          ?.sort((a, b) => {
            if (a.index == null) {
              return 1;
            }
            return a.index - b.index;
          })),
      );
      setResetChildToggle(s => !s);
    }
  }, [childColumnParams]);

  const renderChildTable = (e) => {
    if (isTickets) {
      return <SmartActionCellRenderer
        detailGridOptions={e?.detailGridOptions}
        data={e?.data}
      />;
    }
    return <PlanningCellRenderer detailGridOptions={e?.detailGridOptions} data={e?.data} />;
  };

  const handleDetailSelectionChange = (selectedDetailRow, isSelected) => {
    if (selectedDetailRow) {
      dispatch(setChildSelection({ nodeData: selectedDetailRow, isSelected }));
    }
  };

  useEffect(() => {
    if (handleRowSelect && childSelection) {
      handleRowSelect(childSelection);
      setDisableMasterSelection(!!childSelection?.length);
      setSuppressRowDrag(!!childSelection?.length);
    }
  }, [childSelection?.length]);

  const getChildTableParams = (e) => {
    if (isTickets) {
      return smartActionsCellRendererParams(
        gridRef?.current,
        childColumnDetails,
        childColumnParams,
        rowHeight,
        rowSelection,
        rowDragMultiRow,
        setChildColumnParams,
        handleTableRowSelection,
        setDisableMasterSelection,
        setSuppressRowDrag,
        setSuppressDetailsRowDrag,
        e,
        childTableIds,
        isRowSelectable,
      );
    }
    return detailCellRendererParams(
      gridRef?.current,
      childColumnDetails,
      childColumnParams,
      rowHeight,
      rowSelection,
      rowDragMultiRow,
      setChildColumnParams,
      handleDetailSelectionChange,
      setDisableMasterSelection,
      setSuppressRowDrag,
      setSuppressDetailsRowDrag,
      e,
    );
  };

  const getHeight = () => {
    if (isPlanningBoard) {
      return 'calc(100% - 60px)';
    }
    if (isTickets && isMainTicketPage) {
      return 'calc(100vh - 184px)';
    }
    return 'calc(100vh - 238px)';
  };

  const handleRowGroupOpened = ({ node, data }) => {
    if (data.children?.find(c => c.highlighted) && updatedRowsData?.find(r => r.id === data.id)) {
      node.highlighted = !node.expanded;
      gridRef?.current?.api?.redrawRows({ rowNodes: [node] });
    }
  };

  const cols = useMemo(() =>
    mappedColumns?.filter(c => !c.isHidden)?.sort((a, b) => a.index - b.index), [mappedColumns]);

  return (
    <GridWrapper
      className='ag-theme-alpine'
      suppressMasterRowDrag={suppressRowDrag}
      suppressDetailsRowDrag={suppressDetailsRowDrag}
      disableDetailSelection={disableDetailSelection}
      disableMasterSelection={disableMasterSelection}
      isPlanningBoard={isPlanningBoard}
      isTickets={isTickets}
      height={getHeight()}
    >
      <AgGridReact
        ref={gridRef}
        rowSelection={rowSelection}
        columnDefs={cols}
        isRowMaster={e => !!e?.children.length}
        rowHeight={rowHeight}
        tooltipShowDelay={1000}
        headerHeight={rowHeight}
        rowDragMultiRow={rowDragMultiRow}
        rowMultiSelectWithClick={true}
        detailCellRenderer={renderChildTable}
        detailCellRendererParams={getChildTableParams}
        masterDetail={masterDetail}
        suppressDragLeaveHidesColumns={true}
        suppressCsvExport={true}
        suppressExcelExport={true}
        onSelectionChanged={e => handleSelectionChange(
          e,
          handleTableRowSelection,
          setDisableDetailSelection,
          setSuppressRowDrag,
          setSuppressDetailsRowDrag,
        )}
        onColumnMoved={e => handleColumnVisibilityEvents(e, setColumnParams)}
        onColumnResized={e => handleColumnVisibilityEvents(e, setColumnParams)}
        getRowHeight={p => getRowHeight(p, rowHeight, isTickets, childTableIds)}
        onFirstDataRendered={() =>
          handleFirstRender('columnParams', setColumnParams)
        }
        rowModelType='serverSide'
        suppressServerSideInfiniteScroll={false}
        serverSideDatasource={dataSource}
        maxConcurrentDatasourceRequests={1}
        isRowSelectable={isRowSelectable}
        rowStyle={{
          minWidth: '100vw',
        }}
        rowClassRules={{
          highlighted: params => params.node.highlighted,
        }}
        // This needs to be false, otherwise collapseAll won't work in all cases
        keepDetailRows={false}
        suppressRowClickSelection={true}
        onCellClicked={e =>
          !disableMasterSelection && handleCellClicked(e, 'Description', gridRef?.current, false)
        }
        getRowId={params => params.data.id}
        embedFullWidthRows={true}
        isServerSideGroupOpenByDefault={isServerSideGroupOpenByDefault}
        alwaysMultiSort={true}
        onRowGroupOpened={handleRowGroupOpened}
      />
    </GridWrapper>
  );
};

WorkGrid.propTypes = {
  columnActions: PropTypes.object,
  selectable: PropTypes.bool,
  hasIconsColumn: PropTypes.bool,
  rowSelection: PropTypes.bool,
  rowHeight: PropTypes.number,
  rowDragMultiRow: PropTypes.bool,
  draggable: PropTypes.bool,
  masterDetail: PropTypes.bool,
  handleRowSelect: PropTypes.func,
  columnDef: PropTypes.array,
  isPlanningBoard: PropTypes.bool,
  worklistId: PropTypes.string,
  isTickets: PropTypes.bool,
  isMainTicketPage: PropTypes.bool,
  fixedFilterValues: PropTypes.object,
  selectedIds: PropTypes.array,
};

export default WorkGrid;
